import { DurationString, getDurationStringSeconds } from '@chronosphereio/core';
import { produce } from 'immer';
import {
  Notice,
  NoticeKind,
  PrometheusQuerySpec,
  replaceTemplateVariables,
  VariableStateMap,
  valueToVariableState,
} from '@/core';
import { DataQueryPlugin, QueryPluginContext } from '@/plugin-system';
import { usePluginQueryContext } from '@/plugin-system/runtime/common';
import { adjustStepForScrapeAndIncrease } from '../../model/time';
import {
  extractLabelNamesForVariableFromPromQL,
  injectGlobalFilters as injectGlobalFiltersIntoPromQLQuery,
  injectGroupByLabels,
  injectVariableIntoPromQLQuery,
} from '../lib/promql-variables';

const BUILT_IN_VARIABLE_PREFIX = '__';

/**
 * Calculate the rate interval in seconds for a given interval and scrape interval.
 * Uses the same logic as Grafana:
 * https://github.com/chronosphereio/monorepo/blob/f9273f7e791fb4d4a578effcb759f5ddff247a8b/ui/grafana/src/chronosphere-prometheus/model/global-query-variables.ts#L22
 */
export function calculateRateIntervalSeconds(intervalSeconds: number, scrapeIntervalSeconds: number) {
  // Fall back to the default scrape interval of 15s if scrapeInterval is 0 for some reason.
  if (scrapeIntervalSeconds === 0) {
    scrapeIntervalSeconds = 15;
  }
  const rateIntervalSeconds = Math.max(intervalSeconds + scrapeIntervalSeconds, 4 * scrapeIntervalSeconds);
  return rateIntervalSeconds;
}

export const interpolateVariables = (
  spec: PrometheusQuerySpec,
  ctx: QueryPluginContext,
  args?: {
    includeBuiltInVariables?: boolean;
    isIncreaseWithInterval?: boolean;
  }
) => {
  const { includeBuiltInVariables = true, isIncreaseWithInterval } = args ?? {};
  // First replace the variables in series name and min step
  let interpolatedSpec = produce(spec, (draft) => {
    if (spec.series_name_format) {
      draft.series_name_format = replaceTemplateVariables(spec.series_name_format, ctx.variableState);
    }
    if (draft.series_name && draft.series_name.name_pattern) {
      draft.series_name.name_pattern = replaceTemplateVariables(draft.series_name.name_pattern, ctx.variableState);
    }
    if (spec.min_step) {
      draft.min_step = replaceTemplateVariables(spec.min_step, ctx.variableState) as DurationString;
    }
  });

  const scrapeIntervalSeconds = ctx.config?.prometheusScrapeIntervalSeconds ?? 15;
  const suggestedStepIntervalSeconds = ctx.suggestedStepMs ? Math.round(ctx.suggestedStepMs / 1000) : 15;
  const minStepSeconds = getDurationStringSeconds(interpolatedSpec.min_step as DurationString);

  // Ensure interval seconds is at least the min step seconds
  const intervalSeconds = Math.max(suggestedStepIntervalSeconds, minStepSeconds ?? 0);
  const rateIntervalSeconds = calculateRateIntervalSeconds(intervalSeconds, scrapeIntervalSeconds);

  // Account for whether increase function is used, which requires a minimum step of 2x the scrape interval.
  // Calling after calculateRateIntervalSeconds since rateIntervalSeconds already accounts for the scrape interval.
  // Bypass this logic if an exact ctx.stepMs is passed (ex: when query_options.resolution is a custom duration)
  const adjustedIntervalSeconds = ctx.stepMs
    ? ctx.stepMs / 1000
    : adjustStepForScrapeAndIncrease({
        stepSeconds: intervalSeconds,
        scrapeIntervalSeconds,
        isIncreaseWithInterval,
      });

  // Interpolate the query with built in variables
  interpolatedSpec = produce(interpolatedSpec, (draft) => {
    const variableState: VariableStateMap = {
      ...ctx.variableState,
      __rate_interval: valueToVariableState(`${rateIntervalSeconds}s`),
      // Override interval variables with min step accounted for
      __interval: valueToVariableState(`${adjustedIntervalSeconds}s`),
      __interval_ms: valueToVariableState(`${adjustedIntervalSeconds * 1000}ms`),
    };

    // If we don't want to include built in variables, remove them
    if (!includeBuiltInVariables) {
      Object.keys(variableState).forEach((key) => {
        if (key.startsWith(BUILT_IN_VARIABLE_PREFIX)) {
          delete variableState[key]; // Remove it from the variableState we will use to replace
        }
      });
    }
    draft.query = replaceTemplateVariables(draft.query, {
      ...variableState,
    });
  });

  return interpolatedSpec;
};

export const injectGlobalFilters: Required<DataQueryPlugin<PrometheusQuerySpec>>['injectGlobalFilters'] = async (
  spec,
  ctx
) => {
  const result = await injectGlobalFiltersIntoPromQLQuery(spec.query, ctx.globalFilters ?? []);
  const notices: Notice[] = [];
  const modifiedSpec = produce(spec, (draft) => {
    draft.query = ctx.globalGroupByLabels ? injectGroupByLabels(result.query, ctx.globalGroupByLabels) : result.query;
  });

  result.warnings.forEach((warning) => {
    notices.push({
      type: 'warning',
      message: warning,
      kind: NoticeKind.GlobalFilters,
    });
  });
  return { spec: modifiedSpec, notices };
};

export const injectVariable: Required<DataQueryPlugin<PrometheusQuerySpec>>['injectVariable'] = async (
  spec,
  variable
) => {
  const modifiedQuery = injectVariableIntoPromQLQuery(spec.query, variable);

  const modifiedSpec = produce(spec, (draft) => {
    draft.query = modifiedQuery;
  });

  return { spec: modifiedSpec };
};

export const extractVariableLabels: Required<DataQueryPlugin<PrometheusQuerySpec>>['extractVariableLabels'] = async (
  spec,
  variableName
) => {
  const labelNames = extractLabelNamesForVariableFromPromQL(spec.query, variableName);
  return { labelNames };
};

/**
 * Hook to interpolate variables in a Prometheus time series query spec.
 */
export function useInterpolatePrometheusTimeSeriesQuery(spec: PrometheusQuerySpec) {
  const ctx = usePluginQueryContext();
  return interpolateVariables(spec, ctx);
}
